import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuProps } from "antd";
import {
    FundOutlined,
    LogoutOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
    EnvironmentOutlined,
    BulbOutlined,
    GiftOutlined,
    BarsOutlined,
    SolutionOutlined,
    UnlockOutlined,
    TagOutlined,
    PercentageOutlined,
    MessageOutlined,
    ExclamationCircleOutlined,
    SettingFilled,
    FileSearchOutlined,
} from '@ant-design/icons';
import useAuth from "../../hooks/useAuth";
import useLocalStorage from "../../hooks/useLocalStorage";
import { ThemeContext } from "../../contexts/ThemeContext";
import useModules from "../../hooks/useModules";
import { EModule } from "../../enums";
import HelpAndSupportService from "../../services/http/services/HelpAndSupportService";
import BadgeCount from "./components/BadgeCount";
import AppAuthService from "../../services/http/services/AppAuthService";
import { FaBuilding, FaFileAlt, FaFileInvoice, FaFileInvoiceDollar, FaHandHoldingUsd, FaHeadset, FaMobileAlt } from "react-icons/fa";
import SupportService from "../../services/http/services/SupportService";

interface IMenuItem {
    permissao?: string;
    label: string | React.ReactNode;
    key: string;
    icon: React.ReactNode;
    children?: IMenuItem[];
    route?: string;
}

type MenuItem = Required<MenuProps>['items'][number];

function getMenuKeysFromPath(pathname: string, search: string = ''): { selectedKey: string; parentKey?: string } {
    const cleanPath = pathname.split('?')[0].replace(/\/$/, '') || '/';

    if (cleanPath === '/dashboard' || cleanPath === '') {
        return { selectedKey: 'dashboard' };
    }

    if (cleanPath.startsWith('/ajuda-e-suporte')) {
        return { selectedKey: 'ajuda-e-suporte' };
    }

    if (cleanPath.startsWith('/usuarios/aplicativo')) {
        return { selectedKey: 'usuarios-aplicativo', parentKey: 'usuarios' };
    }
    if (cleanPath.startsWith('/usuarios/auto')) {
        return { selectedKey: 'usuarios-auto', parentKey: 'usuarios' };
    }
    if (cleanPath === '/usuarios') {
        return { selectedKey: 'usuarios-aplicativo', parentKey: 'usuarios' };
    }

    if (cleanPath.startsWith('/sorteios/geral')) {
        return { selectedKey: 'sorteios-geral', parentKey: 'sorteios' };
    }
    if (cleanPath.startsWith('/sorteios/auto')) {
        return { selectedKey: 'sorteios-auto', parentKey: 'sorteios' };
    }
    if (cleanPath.startsWith('/sorteios')) {
        return { selectedKey: 'sorteios-geral', parentKey: 'sorteios' };
    }

    if (cleanPath.startsWith('/impostos/municipais')) {
        return { selectedKey: 'impostos-municipais', parentKey: 'impostos' };
    }
    if (cleanPath.startsWith('/impostos/auto')) {
        return { selectedKey: 'impostos-auto', parentKey: 'impostos' };
    }
    if (cleanPath.startsWith('/impostos')) {
        return { selectedKey: 'impostos-municipais', parentKey: 'impostos' };
    }

    if (cleanPath.startsWith('/instituicoes-sociais')) {
        return { selectedKey: 'instituicoes-sociais' };
    }

    if (cleanPath.startsWith('/notas/geral/importacao')) {
        return { selectedKey: 'notas-geral-importacao', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/notas/estabelecimento')) {
        return { selectedKey: 'notas-estabelecimento', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/notas/contingencia')) {
        return { selectedKey: 'notas-contingencia', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/notas/duplicadas')) {
        return { selectedKey: 'notas-duplicadas', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/notas/geral') || cleanPath.startsWith('/notas/pendente')) {
        return { selectedKey: 'notas-geral', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/nota/detalhes') || cleanPath.startsWith('/nota')) {
        return { selectedKey: 'notas-geral', parentKey: 'notas' };
    }
    if (cleanPath.startsWith('/notas')) {
        return { selectedKey: 'notas-geral', parentKey: 'notas' };
    }

    if (cleanPath.startsWith('/vouchers')) {
        return { selectedKey: 'vouchers' };
    }

    if (cleanPath.startsWith('/recompensas') || cleanPath.startsWith('/categorias')) {
        return { selectedKey: 'recompensas' };
    }

    if (cleanPath.startsWith('/mensagens')) {
        if (search.includes('type=general')) {
            return { selectedKey: 'configuracoes-mensagens', parentKey: 'configuracoes' };
        }
        return { selectedKey: 'mensagens' };
    }

    if (cleanPath.startsWith('/municipios')) {
        return { selectedKey: 'municipios' };
    }

    if (cleanPath.startsWith('/recuperacao-acesso')) {
        return { selectedKey: 'recuperacao-acesso' };
    }

    if (cleanPath.startsWith('/codigo-acesso')) {
        return { selectedKey: 'codigo-acesso' };
    }

    if (cleanPath.startsWith('/avisos')) {
        return { selectedKey: 'avisos', parentKey: 'configuracoes' };
    }

    if (cleanPath.startsWith('/configuracoes/perfis')) {
        return { selectedKey: 'configuracoes-perfis', parentKey: 'configuracoes' };
    }
    if (cleanPath.startsWith('/configuracoes/usuarios-painel')) {
        return { selectedKey: 'configuracoes-usuarios-painel', parentKey: 'configuracoes' };
    }
    if (cleanPath.startsWith('/configuracoes/tipos-de-premios')) {
        return { selectedKey: 'configuracoes-tipos-de-premios', parentKey: 'configuracoes' };
    }
    if (cleanPath.startsWith('/configuracoes/assuntos-ajuda-e-suporte')) {
        return { selectedKey: 'configuracoes-assuntos-ajuda-e-suporte', parentKey: 'configuracoes' };
    }
    if (cleanPath.startsWith('/configuracoes/logs')) {
        return { selectedKey: 'configuracoes-logs', parentKey: 'configuracoes' };
    }
    if (cleanPath.startsWith('/configuracoes')) {
        return { selectedKey: 'configuracoes-perfis', parentKey: 'configuracoes' };
    }

    const segments = cleanPath.split('/').filter(Boolean);
    if (segments.length >= 2) {
        return { selectedKey: `${segments[0]}-${segments[1]}`, parentKey: segments[0] };
    }
    if (segments.length === 1) {
        return { selectedKey: segments[0] };
    }

    return { selectedKey: 'dashboard' };
}

export default function useLayoutAdmin() {
    const navigate = useNavigate();
    const { signOut, handleOpenSelectCityModal, user, activeCity } = useAuth();
    const { getUserId, getSelectedCityFromStorage } = useLocalStorage();
    const { updateTheme } = useContext(ThemeContext);
    const location = useLocation();
    const { cityModules } = useModules();

    const cityId = activeCity?.id || getSelectedCityFromStorage()?.id;

    const [collapsed, setCollapsed] = useState(false);
    const [visibleDrawer, setVisibleDrawer] = useState(false);
    const [visibleDrawerColor, setVisibleDrawerColor] = useState(false);
    const [posicaoMenu, setPosicaoMenu] = useState('');
    const [abrirMenuLateral, setAbrirMenuLateral] = useState<string[]>([]);
    const [collapsedWidth, setCollapsedWidth] = useState<number>(77);

    // Consolidated badge counts state (prevents multiple re-render cascades)
    const [badgeCounts, setBadgeCounts] = useState({
        totalContacts: 0,
        totalAppSupports: 0,
        totalSiteSupports: 0,
        totalRecoverySections: 0,
    });

    const hasAccessOnlyOneCity = useMemo(() => {
        if (user) {
            return user.role?.id !== 1 && user.cities?.length === 1;
        }
        return false;
    }, [user]);

    const clickNavigateMenuLateral = useCallback((rota: string): void => {
        navigate(rota);
        setVisibleDrawer(false);
    }, [navigate]);

    const hasPermissionAndModule = useCallback((permissions: Array<string>, module?: EModule): string => {
        if (module && !cityModules.includes(module)) {
            return String(false);
        }
        return String(!!user?.permissions?.find((p) => permissions.includes(p.name)));
    }, [user, cityModules]);

    const hasCityModulesTax = useMemo(() => {
        return cityModules.includes(EModule.TAXES);
    }, [cityModules]);

    const hasCityModulesSocialInstitution = useMemo(() => {
        return cityModules.includes(EModule.SOCIAL_INSTITUTION);
    }, [cityModules]);

    const hasCityModulesTaxAuto = useMemo(() => {
        return cityModules.includes(EModule.TAXES_AUTO);
    }, [cityModules]);

    const itensMenuPainel: IMenuItem[] = useMemo(() => [
        {
            permissao: String(hasPermissionAndModule(['manage_user_app'])),
            label: 'Dashboard',
            key: 'dashboard',
            icon: <FundOutlined />,
            route: '/dashboard'
        },
        {
            permissao: hasPermissionAndModule(['manage_user_app']),
            label: <BadgeCount title="Ajuda e suporte" total={badgeCounts.totalContacts + badgeCounts.totalAppSupports + (hasPermissionAndModule(['manage_config']) === "true" ? badgeCounts.totalSiteSupports : 0)} />,
            key: 'ajuda-e-suporte',
            icon: <FaHeadset />,
            route: '/ajuda-e-suporte',
        },
        {
            permissao: String(hasCityModulesTaxAuto && hasPermissionAndModule(['manage_user_app'])),
            label: 'Usuários',
            key: 'usuarios',
            icon: <UserOutlined />,
            children: [
                {
                    permissao: hasPermissionAndModule(['manage_user_app']),
                    label: 'Usuários Aplicativo',
                    key: 'usuarios-aplicativo',
                    icon: <UserOutlined />,
                    route: '/usuarios/aplicativo'
                },
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.TAXES_AUTO),
                    label: 'Usuários (IPTU)',
                    key: 'usuarios-auto',
                    icon: <UserOutlined />,
                    route: '/usuarios/auto'
                },
            ]
        },
        {
            permissao: String(!hasCityModulesTaxAuto && hasPermissionAndModule(['manage_user_app'])),
            label: 'Usuários Aplicativo',
            key: 'usuarios-aplicativo',
            icon: <UserOutlined />,
            route: '/usuarios/aplicativo'
        },
        {
            permissao: String(hasCityModulesTaxAuto && hasPermissionAndModule(['manage_raffle'])),
            label: 'Sorteios',
            key: 'sorteios',
            icon: <BulbOutlined />,
            children: [
                {
                    permissao: hasPermissionAndModule(['manage_raffle']),
                    label: 'Sorteios',
                    key: 'sorteios-geral',
                    icon: <BulbOutlined />,
                    route: "/sorteios/geral",
                },
                {
                    permissao: hasPermissionAndModule(['manage_raffle']),
                    label: 'Sorteios (IPTU)',
                    key: 'sorteios-auto',
                    icon: <BulbOutlined />,
                    route: "/sorteios/auto",
                },
            ]
        },
        {
            permissao: String(!hasCityModulesTaxAuto && hasPermissionAndModule(['manage_raffle'])),
            label: 'Sorteios',
            key: 'sorteios-geral',
            icon: <BulbOutlined />,
            route: "/sorteios/geral",
        },
        {
            permissao: String(hasCityModulesTax && hasCityModulesTaxAuto),
            label: 'Impostos',
            key: 'impostos',
            icon: <PercentageOutlined />,
            children: [
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.TAXES),
                    label: 'Impostos municipais',
                    key: 'impostos-municipais',
                    icon: <PercentageOutlined />,
                    route: "/impostos/municipais",
                },
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.TAXES_AUTO),
                    label: 'Impostos (IPTU)',
                    key: 'impostos-auto',
                    icon: <PercentageOutlined />,
                    route: "/impostos/auto",
                },
            ]
        },
        {
            permissao: String(hasCityModulesTax && !hasCityModulesTaxAuto && hasPermissionAndModule(['manage_user_app'], EModule.TAXES)),
            label: 'Impostos municipais',
            key: 'impostos-municipais',
            icon: <PercentageOutlined />,
            route: "/impostos/municipais",
        },
        {
            permissao: String(hasCityModulesSocialInstitution && hasPermissionAndModule(['manage_user_app'], EModule.SOCIAL_INSTITUTION)),
            label: 'Instituições Sociais',
            key: 'instituicoes-sociais',
            icon: <FaHandHoldingUsd />,
            route: "/instituicoes-sociais",
        },
        {
            permissao: String(!hasCityModulesTax && hasCityModulesTaxAuto && hasPermissionAndModule(['manage_user_app'], EModule.TAXES_AUTO)),
            label: 'Impostos (IPTU)',
            key: 'impostos-auto',
            icon: <PercentageOutlined />,
            route: "/impostos/auto",
        },
        {
            permissao: hasPermissionAndModule(['manage_user_app'], EModule.INVOICE_NFC),
            label: 'Notas fiscais',
            key: 'notas',
            icon: <FaFileInvoice />,
            children: [
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.INVOICE_NFC),
                    label: 'Todas',
                    key: 'notas-geral',
                    icon: <FaFileInvoiceDollar />,
                    route: "/notas/geral",
                },
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.INVOICE_NFC),
                    label: 'Por estabelecimento',
                    key: 'notas-estabelecimento',
                    icon: <FaBuilding />,
                    route: "/notas/estabelecimento",
                },
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.INVOICE_NFC),
                    label: 'Em contingência',
                    key: 'notas-contingencia',
                    icon: <FaFileInvoiceDollar />,
                    route: "/notas/contingencia",
                },
                {
                    permissao: hasPermissionAndModule(['manage_config'], EModule.INVOICE_NFC),
                    label: 'Duplicadas',
                    key: 'notas-duplicadas',
                    icon: <FileSearchOutlined />,
                    route: "/notas/duplicadas",
                },
                {
                    permissao: hasPermissionAndModule(['manage_user_app'], EModule.INVOICE_NFS),
                    label: 'Notas importadas',
                    key: 'notas-geral-importacao',
                    icon: <FaFileAlt />,
                    route: "/notas/geral/importacao",
                },
            ],
        },
        {
            permissao: hasPermissionAndModule(['manage_reward'], EModule.REWARD),
            label: 'Vouchers',
            key: 'vouchers',
            icon: <TagOutlined />,
            route: "/vouchers",
        },
        {
            permissao: hasPermissionAndModule(['manage_reward'], EModule.REWARD),
            label: 'Recompensas',
            key: 'recompensas',
            icon: <GiftOutlined />,
            route: "/recompensas",
        },
        {
            permissao: hasPermissionAndModule(['manage_user_app']),
            label: 'Mensagens',
            key: 'mensagens',
            icon: <MessageOutlined />,
            route: '/mensagens'
        },
        {
            permissao: hasPermissionAndModule(['manage_county']),
            label: hasAccessOnlyOneCity ? 'Município' : 'Municípios',
            key: 'municipios',
            icon: <EnvironmentOutlined />,
            route: hasAccessOnlyOneCity ? "/municipios/editar/" + cityId : "/municipios"
        },
        {
            permissao: hasPermissionAndModule(['manage_user_app']),
            label: <BadgeCount title="Solicitações Rec. Acesso" total={badgeCounts.totalRecoverySections} />,
            key: 'recuperacao-acesso',
            icon: <UnlockOutlined />,
            route: '/recuperacao-acesso',
        },
        {
            permissao: hasPermissionAndModule(['manage_config']),
            label: "Códigos Autenticação",
            key: 'codigo-acesso',
            icon: <FaMobileAlt />,
            route: '/codigo-acesso',
        },
        {
            permissao: hasPermissionAndModule(['manage_config']),
            label: 'Configurações',
            key: 'configuracoes',
            icon: <SettingOutlined />,
            children: [
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Perfis',
                    key: 'configuracoes-perfis',
                    icon: <SolutionOutlined />,
                    route: '/configuracoes/perfis',
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Usuários Painel',
                    key: 'configuracoes-usuarios-painel',
                    icon: <TeamOutlined />,
                    route: '/configuracoes/usuarios-painel'
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Mensagens Gerais',
                    key: 'configuracoes-mensagens',
                    icon: <MessageOutlined />,
                    route: '/mensagens?type=general',
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Avisos/Notícias',
                    key: 'avisos',
                    icon: <ExclamationCircleOutlined />,
                    route: '/avisos'
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Tipos de Prêmios',
                    key: 'configuracoes-tipos-de-premios',
                    icon: <BarsOutlined />,
                    route: '/configuracoes/tipos-de-premios'
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Assuntos',
                    key: 'configuracoes-assuntos-ajuda-e-suporte',
                    icon: <MessageOutlined />,
                    route: '/configuracoes/assuntos-ajuda-e-suporte'
                },
                {
                    permissao: hasPermissionAndModule(['manage_config']),
                    label: 'Logs',
                    key: 'configuracoes-logs',
                    icon: <SettingFilled />,
                    route: '/configuracoes/logs'
                }
            ]
        }
    ], [
        badgeCounts,
        cityId,
        hasAccessOnlyOneCity,
        hasCityModulesTax,
        hasCityModulesTaxAuto,
        hasPermissionAndModule,
        hasCityModulesSocialInstitution,
    ]);

    const itensMenuConfig: IMenuItem[] = useMemo(() => [
        {
            permissao: 'true',
            label: 'Meu Perfil',
            key: 'perfil1',
            icon: <UserOutlined />
        },
        {
            permissao: String(user?.cities && user.cities.length > 1),
            label: 'Alterar Município',
            key: 'municipio1',
            icon: <EnvironmentOutlined />
        },
        {
            permissao: 'true',
            label: 'Sair',
            key: 'sair1',
            icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />
        }
    ], [user]);

    const filterMenuByPermission = useCallback((items: IMenuItem[]): IMenuItem[] => {
        return items
            .filter(item => item.permissao === 'true')
            .map(item => ({
                ...item,
                children: item.children
                    ? filterMenuByPermission(item.children)
                    : undefined
            }));
    }, []);

    const itemsMenuPainelState = useMemo<MenuItem[]>(() => {
        return filterMenuByPermission(itensMenuPainel) as MenuItem[];
    }, [itensMenuPainel, filterMenuByPermission]);

    const itemsMenuConfiguracaoState = useMemo<MenuItem[]>(() => {
        return itensMenuConfig.filter((element: any) => element?.permissao === 'true') as MenuItem[];
    }, [itensMenuConfig]);

    const nomeUsuarioLogado = useMemo(() => {
        if (!user?.name) return '';
        return user.name.split(" ")[0];
    }, [user?.name]);

    const onCloseDrawer = useCallback((): void => {
        setVisibleDrawer(false);
    }, []);

    useEffect(() => {
        if (!cityId) return;
        const currentCityId = cityId;

        const abortController = new AbortController();

        async function fetchBadgeCounts() {
            try {
                const [totalContacts, totalAppSupport, totalSiteSupport, recoverySections] = await Promise.all([
                    HelpAndSupportService.getTotalContacts(currentCityId, abortController.signal).catch(() => 0),
                    SupportService.getTotalSupport({ cityId: currentCityId, origin: 'aplicativo', signal: abortController.signal }).catch(() => 0),
                    SupportService.getTotalSupport({ cityId: currentCityId, origin: 'site', signal: abortController.signal }).catch(() => 0),
                    AppAuthService.getTotalRecoverySectionsWithoutResponse(currentCityId, abortController.signal).catch(() => 0),
                ]);

                setBadgeCounts({
                    totalContacts: totalContacts || 0,
                    totalAppSupports: totalAppSupport || 0,
                    totalSiteSupports: totalSiteSupport || 0,
                    totalRecoverySections: recoverySections || 0,
                });
            } catch (err) {
            }
        }

        fetchBadgeCounts();

        return () => {
            abortController.abort();
        };
    }, [cityId]);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            setCollapsedWidth(0);
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const { selectedKey, parentKey } = getMenuKeysFromPath(location.pathname, location.search);
        setPosicaoMenu(selectedKey);

        if (parentKey) {
            setAbrirMenuLateral((prevKeys) => {
                if (prevKeys.includes(parentKey)) {
                    return prevKeys;
                }
                return [...prevKeys, parentKey];
            });
        }
    }, [location.pathname, location.search]);

    const onCollapse = useCallback((collapsedVal: boolean): void => {
        if (!collapsed) {
            setCollapsed(collapsedVal);
            setCollapsedWidth(77);
        } else {
            setCollapsed(collapsedVal);
            setCollapsedWidth(0);
        }
    }, [collapsed]);

    const onAbrirDrawerColor = useCallback((): void => {
        setVisibleDrawerColor(true);
    }, []);

    const onCloseDrawerColor = useCallback((): void => {
        setVisibleDrawerColor(false);
    }, []);

    const toggle = useCallback((): void => {
        if (collapsedWidth === 0) {
            setVisibleDrawer((prev) => !prev);
        } else {
            setCollapsed((prev) => !prev);
        }
    }, [collapsedWidth]);

    const clickMenuDireitoUsuarios = useCallback((): void => {
        navigate("/usuarios");
    }, [navigate]);

    const clickMenuPerfilUsuario = useCallback((): void => {
        const id = getUserId();
        navigate("/perfil/" + id);
    }, [getUserId, navigate]);

    const clickMenuDireitoChangeCity = useCallback((): void => {
        handleOpenSelectCityModal();
    }, [handleOpenSelectCityModal]);

    const clickMenuDireitoSair = useCallback((): void => {
        signOut();
    }, [signOut]);

    const onClickMenuConfiguracoes = useCallback((e: any): void => {
        switch (e.key) {
            case 'perfil1':
                clickMenuPerfilUsuario();
                break;
            case 'usuarios1':
                clickMenuDireitoUsuarios();
                break;
            case 'tema1':
                onAbrirDrawerColor();
                break;
            case 'municipio1':
                clickMenuDireitoChangeCity();
                break;
            case 'sair1':
                clickMenuDireitoSair();
                break;
            default:
                break;
        }
    }, [clickMenuDireitoSair, clickMenuDireitoUsuarios, clickMenuPerfilUsuario, clickMenuDireitoChangeCity, onAbrirDrawerColor]);

    const onchangeRadioTheme = useCallback((value: any): void => {
        updateTheme(value.target.value);
    }, [updateTheme]);

    const clickMenuPrincipal = useCallback(({ keyPath }: { keyPath: string[] }): void => {
        setPosicaoMenu(keyPath[0]);

        const resolvedKeyPath = [...keyPath];
        if (resolvedKeyPath.length > 1) {
            resolvedKeyPath.reverse();
        }

        const menuItem = itensMenuPainel.find((item) => item.key === resolvedKeyPath[0]);

        if (!menuItem) {
            return;
        }

        if (menuItem.route) {
            clickNavigateMenuLateral(menuItem.route);
            return;
        }

        const menuChildItem = menuItem.children?.find((childItem) => childItem.key === resolvedKeyPath[1]);

        if (menuChildItem?.route) {
            clickNavigateMenuLateral(menuChildItem.route);
            return;
        }
    }, [clickNavigateMenuLateral, itensMenuPainel]);

    return {
        onCloseDrawer,
        visibleDrawer,
        clickMenuPrincipal,
        posicaoMenu,
        itemsMenuPainelState,
        collapsedWidth,
        onCollapse,
        collapsed,
        setAbrirMenuLateral,
        abrirMenuLateral,
        onClickMenuConfiguracoes,
        toggle,
        itemsMenuConfiguracaoState,
        nomeUsuarioLogado,
        onCloseDrawerColor,
        visibleDrawerColor,
        onchangeRadioTheme,
    };
}
